import type {
    ClientInstance,
    ModuleMountDeps,
    ModuleRegistration,
    Nullable,
} from "../../types";

/**
 * Manages client module registrations and mounts sub-clients onto parent
 * {@link BackendClient} instances.
 *
 * Maintains a two-level map (`pluginId -> moduleId -> registration`) and handles
 * the dot-path mounting of sub-clients onto parent client instances.
 *
 * @public
 */
export class ClientRegistry {
    /** Two-level map of `pluginId -> moduleId -> registration`. */
    readonly #registry = new Map<string, Map<string, ModuleRegistration>>();

    /** Re-entrancy guard that prevents recursive mount calls. */
    #mounting = false;

    /**
     * Adds or replaces a module registration.
     *
     * @param registration - The module registration descriptor.
     */
    register(registration: ModuleRegistration): void {
        const { pluginId, moduleId } = registration;
        if (!this.#registry.has(pluginId)) {
            this.#registry.set(pluginId, new Map());
        }
        this.#registry.get(pluginId)!.set(moduleId, registration);
    }

    /**
     * Instantiates and mounts all registered sub-clients for a plugin onto the
     * given target object at the paths declared in each registration.
     *
     * @param target - The client instance to mount sub-clients onto (cast to record).
     * @param pluginId - The plugin to look up registrations for.
     * @param deps - The parent client's discovery and identity APIs, forwarded to
     *   each sub-client constructor.
     */
    mount(
        target: Record<string, unknown>,
        pluginId: string,
        deps: ModuleMountDeps
    ): void {
        if (this.#mounting) return;
        const modules = this.#registry.get(pluginId);
        if (!modules) return;

        this.#mounting = true;
        try {
            modules.forEach(reg => {
                // Split "a.b.c" into segments; the last segment is the leaf key.
                const parts = reg.path.split(".");
                const leafKey = parts[parts.length - 1];

                // Walk all but the last segment, creating intermediate namespace
                // objects on the target as needed.
                const parent = this.#resolveParent(target, parts.slice(0, -1));

                // Instantiate the sub-client with the parent's deps.
                const ClientExtension = reg.clientExtension;
                const instance = new ClientExtension(deps);

                // If the leaf already holds a plain object (e.g. another module
                // already mounted at this path), merge methods onto it.
                // Otherwise assign the sub-client instance directly.
                if (this.#isPlainObject(parent, leafKey)) {
                    this.#mergeClientMethods(
                        parent[leafKey] as Record<string, unknown>,
                        instance
                    );
                } else {
                    parent[leafKey] = instance;
                }
            });
        } finally {
            this.#mounting = false;
        }
    }

    /** Removes all registrations across all plugins. Useful for test isolation. */
    clear(): void {
        this.#registry.clear();
    }

    /**
     * Returns `true` when `key` is present on `target`, its value is an object,
     * and it is not `null`.
     */
    #isPlainObject(target: Record<string, unknown>, key: string): boolean {
        return (
            key in target &&
            typeof target[key] === "object" &&
            target[key] !== null
        );
    }

    /**
     * Walks `segments` on `target`, creating intermediate plain objects as needed,
     * and returns the final resolved namespace object.
     */
    #resolveParent(
        target: Record<string, unknown>,
        segments: string[]
    ): Record<string, unknown> {
        return segments.reduce<Record<string, unknown>>((current, part) => {
            if (!this.#isPlainObject(current, part)) {
                current[part] = {};
            }
            return current[part] as Record<string, unknown>;
        }, target);
    }

    /** Returns the constructor name of `instance`. */
    #getClassName(instance: object): string {
        return (instance as ClientInstance).constructor.name;
    }

    /**
     * Binds all prototype methods from `instance` (up to but not including
     * `Object.prototype`) onto `target`, preserving the original `this` context.
     *
     * Allows multiple modules to contribute methods to a shared namespace object.
     */
    #mergeClientMethods(
        target: Record<string, unknown>,
        instance: object
    ): void {
        // Walk up the prototype chain, stopping before Object.prototype,
        // to capture all class-defined methods across the inheritance hierarchy.
        let proto: Nullable<object> = Object.getPrototypeOf(instance);
        while (proto !== null && proto !== Object.prototype) {
            for (const key of Object.getOwnPropertyNames(proto)) {
                if (key === "constructor") continue;
                const descriptor = Object.getOwnPropertyDescriptor(proto, key);
                // Only bind plain function methods -- skip getters, setters, and non-functions.
                if (descriptor && typeof descriptor.value === "function") {
                    if (key in target) {
                        throw new Error(
                            `[ClientRegistry] Method "${key}" already exists on the merged target. ` +
                                `Module "${this.#getClassName(
                                    instance
                                )}" cannot register a duplicate method name at the same path.`
                        );
                    }
                    // Bind to the sub-client instance so `this` is preserved when called.
                    target[key] = (
                        descriptor.value as (...args: unknown[]) => unknown
                    ).bind(instance);
                }
            }
            proto = Object.getPrototypeOf(proto);
        }
    }
}

/** Shared registry instance used by all {@link BackendClient} instances. */
export const clientRegistry = new ClientRegistry();

/**
 * Creates and immediately registers a typed module descriptor in the shared
 * {@link clientRegistry}.
 *
 * Calling this function at module evaluation time (e.g. as a default export) is
 * the only step needed to wire a module sub-client into any
 * {@link BackendClient} subclass -- no separate registration call is required.
 *
 * @example
 * ```typescript
 * export const googleModuleGcpSecurityFindingsClientModule = createClientModule({
 *   pluginId: "google",
 *   moduleId: "gcp-security-findings",
 *   path: "gcp.securityFindings",
 *   clientExtension: GoogleGcpSecurityFindingsClient,
 * });
 * ```
 *
 * @public
 */
export function createClientModule(
    registration: ModuleRegistration
): ModuleRegistration {
    clientRegistry.register(registration);
    return registration;
}
